import { filter, isNotNil } from 'ramda'

import { UnexpectErrorAcronym } from '@/constants/errors'
import { ApiPrefix, ErrorPrefix } from '@/constants/errors/prefix'
import {
  ApiSuffix,
  DHPServiceSuffix,
  PageSuffix,
} from '@/constants/errors/suffix'

export type ErrorCodeDetails = {
  prefix?: ErrorPrefix | ApiPrefix | UnexpectErrorAcronym
  suffix?: PageSuffix | DHPServiceSuffix | ApiSuffix | UnexpectErrorAcronym
  httpStatusCode?: number | string
  code?: string
}

/**
 * The ErrorCode class is the base class for all errors in the app.
 *
 * This class encapsulates the root error and uses it to create the Error Code structure.
 *
 * Use this to build your custom error code classes.
 *
 * @see docs/ERRORS.md and docs/ERROR_CODES.md for more details.
 */
export class ErrorCode extends Error {
  /**
   * @param details is an optional object that holds additional information about the error.
   * @param errorPayload is an optional object that holds the original error payload, useful for debugging.
   */
  constructor(
    protected readonly details?: ErrorCodeDetails,
    readonly errorPayload?: unknown,
  ) {
    super()
  }

  /**
   * Encapsulates the details object passed to the constructor and returns it.
   * The encapsulation is useful to turn the details into a runtime mutable object
   * if needed be constructed further down the inheritance chain.
   */
  getErrorCodeDetails(): ErrorCodeDetails {
    return this.details || {}
  }

  /**
   * This is a calculated property that generates the error code based on the details
   * passed to the constructor or getErrorCodeDetails implementation.
   */
  get errorCode(): string {
    const {
      prefix = 'XX',
      httpStatusCode = 'XXX',
      code = 'UNEXPECTED',
      suffix = 'XX',
    } = filter(isNotNil, this.getErrorCodeDetails())

    return `${prefix}_${httpStatusCode.toString()}_${code}_${suffix}`
  }

  get message(): string {
    return `Error Code: ${this.errorCode}`
  }
}
